import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  BadgeCheck,
  CalendarDays,
  Camera,
  Loader2,
  Mail,
  Pencil,
  Phone,
  RefreshCcw,
  Save,
  UserRound,
  X,
} from "lucide-react";

import { useGlobalContext } from "../../Context/GlobalContext";

import { useProfile } from "../../Services/Profile/useProfile";

/* =====================================================
   HELPERS
===================================================== */

const normalizeString = (value) => String(value ?? "").trim();

/* =====================================================
   FORMAT DATE
===================================================== */

const formatDate = (value) => {
  const normalizedValue = normalizeString(value);

  if (!normalizedValue) {
    return "Not added";
  }

  const date = new Date(normalizedValue);

  if (Number.isNaN(date.getTime())) {
    return normalizedValue;
  }

  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
};

/* =====================================================
   CALCULATE AGE
===================================================== */

const calculateAge = (dateOfBirth) => {
  if (!dateOfBirth) {
    return "";
  }

  const birthDate = new Date(dateOfBirth);

  if (Number.isNaN(birthDate.getTime())) {
    return "";
  }

  const today = new Date();

  let age = today.getFullYear() - birthDate.getFullYear();

  const monthDifference = today.getMonth() - birthDate.getMonth();

  if (
    monthDifference < 0 ||
    (monthDifference === 0 && today.getDate() < birthDate.getDate())
  ) {
    age -= 1;
  }

  return age >= 0 ? String(age) : "";
};

/* =====================================================
   EMAIL VALIDATION
===================================================== */

const isValidEmail = (value) => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizeString(value));
};

/* =====================================================
   GET TODAY FOR DOB MAX
===================================================== */

const getTodayDate = () => {
  const today = new Date();

  const year = today.getFullYear();

  const month = String(today.getMonth() + 1).padStart(2, "0");

  const day = String(today.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

/* =====================================================
   STORAGE PROFILE
===================================================== */

const getStoredProfile = () => {
  if (typeof window === "undefined") {
    return {};
  }

  try {
    const storedCustomer =
      sessionStorage.getItem("customer") || localStorage.getItem("customer");

    const parsedCustomer = storedCustomer ? JSON.parse(storedCustomer) : {};

    return {
      user_code:
        normalizeString(sessionStorage.getItem("user_code")) ||
        normalizeString(localStorage.getItem("user_code")) ||
        normalizeString(parsedCustomer?.user_code),

      first_name:
        normalizeString(sessionStorage.getItem("first_name")) ||
        normalizeString(localStorage.getItem("first_name")) ||
        normalizeString(parsedCustomer?.first_name),

      last_name:
        normalizeString(sessionStorage.getItem("last_name")) ||
        normalizeString(localStorage.getItem("last_name")) ||
        normalizeString(parsedCustomer?.last_name),

      username:
        normalizeString(sessionStorage.getItem("username")) ||
        normalizeString(localStorage.getItem("username")) ||
        normalizeString(parsedCustomer?.username),

      email:
        normalizeString(sessionStorage.getItem("email")) ||
        normalizeString(localStorage.getItem("email")) ||
        normalizeString(parsedCustomer?.email),

      phone:
        normalizeString(sessionStorage.getItem("phone")) ||
        normalizeString(localStorage.getItem("phone")) ||
        normalizeString(parsedCustomer?.phone) ||
        normalizeString(parsedCustomer?.number),

      date_of_birth:
        normalizeString(sessionStorage.getItem("date_of_birth")) ||
        normalizeString(localStorage.getItem("date_of_birth")) ||
        normalizeString(parsedCustomer?.date_of_birth),

      gender:
        normalizeString(sessionStorage.getItem("gender")) ||
        normalizeString(localStorage.getItem("gender")) ||
        normalizeString(parsedCustomer?.gender),

      age:
        normalizeString(sessionStorage.getItem("age")) ||
        normalizeString(localStorage.getItem("age")) ||
        normalizeString(parsedCustomer?.age),

      profile_image:
        normalizeString(sessionStorage.getItem("profile_image")) ||
        normalizeString(localStorage.getItem("profile_image")) ||
        normalizeString(parsedCustomer?.profile_image) ||
        normalizeString(parsedCustomer?.profileImage),

      privacy_policy:
        normalizeString(sessionStorage.getItem("privacy_policy")) ||
        normalizeString(localStorage.getItem("privacy_policy")) ||
        normalizeString(parsedCustomer?.privacy_policy),
    };
  } catch (error) {
    console.error("Unable to read stored profile:", error);

    return {};
  }
};

/* =====================================================
   SAVE EXTRA LOCAL PROFILE FIELDS
===================================================== */

const saveExtraProfileFields = ({ gender, profileImage }) => {
  try {
    if (gender) {
      sessionStorage.setItem("gender", gender);
    } else {
      sessionStorage.removeItem("gender");
    }

    if (profileImage) {
      sessionStorage.setItem("profile_image", profileImage);
    } else {
      sessionStorage.removeItem("profile_image");
    }

    const storedCustomer = sessionStorage.getItem("customer");

    const parsedCustomer = storedCustomer ? JSON.parse(storedCustomer) : {};

    sessionStorage.setItem(
      "customer",
      JSON.stringify({
        ...(parsedCustomer || {}),

        gender: gender || null,

        profile_image: profileImage || null,
      }),
    );
  } catch (error) {
    console.error("Unable to save local profile fields:", error);
  }
};

/* =====================================================
   PROFILE FIELD
===================================================== */

const ProfileField = ({ icon: Icon, label, value }) => {
  return (
    <div className="min-w-0 rounded-2xl border border-gray-100 bg-[#fafbfc] p-3.5 sm:p-4 lg:p-5">
      <div className="flex min-w-0 items-start gap-2">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white text-[#21195A] shadow-sm sm:h-10 sm:w-10">
          <Icon size={18} />
        </div>

        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-medium uppercase tracking-wide text-[#95a1b3] sm:text-xs">
            {label}
          </p>

          <p className="mt-1 break-words text-sm font-semibold leading-5 text-gray-800 sm:text-[15px] sm:leading-6">
            {value || "Not added"}
          </p>
        </div>
      </div>
    </div>
  );
};

/* =====================================================
   PROFILE
===================================================== */

export default function Profile() {
  const { user, customer, setUser } = useGlobalContext() || {};

const {
  profileDetails,

  getProfileDetails,

  updateProfileDetails,

  uploadProfileImage,

  profileLoading,
  profileError,

  setProfileError,
} = useProfile();

  /* =====================================================
     FILE INPUT
  ===================================================== */

  const profileImageInputRef = useRef(null);

  /* =====================================================
     STORED PROFILE
  ===================================================== */

  const storedProfile = useMemo(() => getStoredProfile(), []);

  /* =====================================================
     USER CODE
  ===================================================== */

  const userCode = normalizeString(
    user?.user_code ||
      user?.userCode ||
      customer?.user_code ||
      customer?.customerId ||
      storedProfile?.user_code,
  );

  /* =====================================================
     PROFILE STATE
  ===================================================== */

  const [profile, setProfile] = useState(() => ({
    ...storedProfile,
  }));

  const [isEditOpen, setIsEditOpen] = useState(false);

  /* =====================================================
     FORM STATE
  ===================================================== */

  const [firstName, setFirstName] = useState(
    normalizeString(storedProfile?.first_name),
  );

  const [lastName, setLastName] = useState(
    normalizeString(storedProfile?.last_name),
  );

  const [username, setUsername] = useState(
    normalizeString(storedProfile?.username),
  );

  const [email, setEmail] = useState(normalizeString(storedProfile?.email));

  const [dateOfBirth, setDateOfBirth] = useState(
    normalizeString(storedProfile?.date_of_birth),
  );

  const [gender, setGender] = useState(normalizeString(storedProfile?.gender));

  const [profileImage, setProfileImage] = useState(
    normalizeString(storedProfile?.profile_image),
  );

  /* =====================================================
     MESSAGES
  ===================================================== */

  const [localError, setLocalError] = useState("");

  const [successMessage, setSuccessMessage] = useState("");

  const [imageError, setImageError] = useState("");

  /* =====================================================
     LOAD PROFILE
  ===================================================== */

  const loadProfile = useCallback(async () => {
    if (!userCode) {
      setLocalError(
        "User code is not available. Please logout and login again.",
      );

      return;
    }

    setLocalError("");
    setSuccessMessage("");
    setProfileError("");

    const result = await getProfileDetails({
      userCode,
    });

    if (!result?.success) {
      setLocalError(result?.message || "Unable to load profile details.");

      return;
    }

    /*
     * API profile ko local extra
     * gender/image ke saath merge
     * kar rahe hain.
     */
    const localProfile = getStoredProfile();

    const nextProfile = {
      ...localProfile,
      ...(result?.data || {}),
    };

    setProfile(nextProfile);

    setFirstName(normalizeString(nextProfile?.first_name));

    setLastName(normalizeString(nextProfile?.last_name));

    setUsername(
      normalizeString(
        nextProfile?.username ||
          [nextProfile?.first_name, nextProfile?.last_name]
            .filter(Boolean)
            .join(" "),
      ),
    );

    setEmail(normalizeString(nextProfile?.email));

    setDateOfBirth(normalizeString(nextProfile?.date_of_birth));

    setAge(
      normalizeString(
        nextProfile?.age || calculateAge(nextProfile?.date_of_birth),
      ),
    );

    setGender(normalizeString(nextProfile?.gender));

    setProfileImage(normalizeString(nextProfile?.profile_image));
  }, [getProfileDetails, setProfileError, userCode]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  /* =====================================================
     PROFILE DETAILS CHANGE
  ===================================================== */

  useEffect(() => {
    if (!profileDetails) {
      return;
    }

    const localProfile = getStoredProfile();

    const nextProfile = {
      ...localProfile,
      ...profileDetails,
    };

    setProfile(nextProfile);

    setFirstName(normalizeString(nextProfile?.first_name));

    setLastName(normalizeString(nextProfile?.last_name));

    setUsername(
      normalizeString(
        nextProfile?.username ||
          [nextProfile?.first_name, nextProfile?.last_name]
            .filter(Boolean)
            .join(" "),
      ),
    );

    setEmail(normalizeString(nextProfile?.email));

    setDateOfBirth(normalizeString(nextProfile?.date_of_birth));

    setAge(
      normalizeString(
        nextProfile?.age || calculateAge(nextProfile?.date_of_birth),
      ),
    );

    setGender(normalizeString(nextProfile?.gender));

    setProfileImage(normalizeString(nextProfile?.profile_image));
  }, [profileDetails]);

  /* =====================================================
     NAME
  ===================================================== */

  const fullName = [
    normalizeString(profile?.first_name),

    normalizeString(profile?.last_name),
  ]
    .filter(Boolean)
    .join(" ");

  /* =====================================================
     INITIALS
  ===================================================== */

  const profileInitials =
    [
      normalizeString(profile?.first_name).charAt(0),

      normalizeString(profile?.last_name).charAt(0),
    ]
      .join("")
      .toUpperCase() || "U";

  const editProfileInitials =
    [normalizeString(firstName).charAt(0), normalizeString(lastName).charAt(0)]
      .join("")
      .toUpperCase() || "U";

  /* =====================================================
     AGE
  ===================================================== */

  const [age, setAge] = useState(
    normalizeString(
      storedProfile?.age || calculateAge(storedProfile?.date_of_birth),
    ),
  );

  /* =====================================================
     MODAL BODY SCROLL LOCK + ESC CLOSE
  ===================================================== */

  useEffect(() => {
    if (!isEditOpen || typeof document === "undefined") {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape" && !profileLoading) {
        setIsEditOpen(false);
        setLocalError("");
        setImageError("");
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isEditOpen, profileLoading]);

  /* =====================================================
     OPEN EDIT
  ===================================================== */

  const handleOpenEdit = () => {
    const currentFirstName = normalizeString(profile?.first_name);

    const currentLastName = normalizeString(profile?.last_name);

    setFirstName(currentFirstName);

    setLastName(currentLastName);

    setUsername(
      normalizeString(
        profile?.username || `${currentFirstName} ${currentLastName}`,
      ),
    );

    setEmail(normalizeString(profile?.email));

    setDateOfBirth(normalizeString(profile?.date_of_birth));

    setAge(
      normalizeString(profile?.age || calculateAge(profile?.date_of_birth)),
    );

    setGender(normalizeString(profile?.gender));

    setProfileImage(normalizeString(profile?.profile_image));

    setImageError("");

    setLocalError("");

    setSuccessMessage("");

    setProfileError("");

    setIsEditOpen(true);
  };

  /* =====================================================
     CLOSE EDIT
  ===================================================== */

  const handleCloseEdit = () => {
    if (profileLoading) {
      return;
    }

    setLocalError("");

    setImageError("");

    setIsEditOpen(false);
  };

  /* =====================================================
     PROFILE IMAGE
  ===================================================== */

  const handleProfileImageSelect = (event) => {
    const file = event.target.files?.[0];

    event.target.value = "";

    if (!file) {
      return;
    }

    setImageError("");

    if (!file.type.startsWith("image/")) {
      setImageError("Please select a valid image.");

      return;
    }

    const maximumSize = 2 * 1024 * 1024;

    if (file.size > maximumSize) {
      setImageError("Profile image must be smaller than 2 MB.");

      return;
    }

    const reader = new FileReader();

    reader.onload = () => {
      setProfileImage(String(reader.result || ""));
    };

    reader.onerror = () => {
      setImageError("Unable to read selected image.");
    };

    reader.readAsDataURL(file);
  };

  /* =====================================================
     FIRST NAME
  ===================================================== */

  const handleFirstNameChange = (event) => {
    const value = event.target.value.replace(/[^a-zA-Z\s'-]/g, "").slice(0, 40);

    setFirstName(value);
    setLocalError("");
  };

  /* =====================================================
     LAST NAME
  ===================================================== */

  const handleLastNameChange = (event) => {
    const value = event.target.value.replace(/[^a-zA-Z\s'-]/g, "").slice(0, 40);

    setLastName(value);
    setLocalError("");
  };

  /* =====================================================
     SAVE PROFILE
  ===================================================== */

  const handleSaveProfile = async (event) => {
    event.preventDefault();

    const cleanFirstName = normalizeString(firstName);

    const cleanLastName = normalizeString(lastName);

    const cleanUsername = normalizeString(
      username || `${cleanFirstName} ${cleanLastName}`,
    );

    const cleanEmail = normalizeString(email);

    const cleanDateOfBirth = normalizeString(dateOfBirth);

    const cleanGender = normalizeString(gender);

    setLocalError("");

    setSuccessMessage("");

    setProfileError("");

    /* =================================================
         VALIDATION
      ================================================= */

    if (!userCode) {
      setLocalError("User code is not available.");

      return;
    }

    if (!cleanFirstName) {
      setLocalError("First name is required.");

      return;
    }

    if (!cleanLastName) {
      setLocalError("Last name is required.");

      return;
    }

    if (!cleanUsername) {
      setLocalError("Username is required.");

      return;
    }

    if (!cleanEmail) {
      setLocalError("Email is required.");

      return;
    }

    if (!isValidEmail(cleanEmail)) {
      setLocalError("Please enter a valid email address.");

      return;
    }

    if (cleanDateOfBirth) {
      const dobDate = new Date(cleanDateOfBirth);

      const today = new Date();

      if (Number.isNaN(dobDate.getTime())) {
        setLocalError("Please enter a valid date of birth.");

        return;
      }

      if (dobDate > today) {
        setLocalError("Date of birth cannot be in the future.");

        return;
      }
    }

    try {
      // =================================================
      // PROFILE IMAGE UPLOAD
      // =================================================

      let finalProfileImage = normalizeString(profileImage);

      /*
       * New selected image FileReader se
       * data:image/...;base64 format me hoti hai.
       *
       * Existing server URL ko dobara upload nahi karenge.
       */
      if (finalProfileImage.startsWith("data:image/")) {
        const imageUploadResult = await uploadProfileImage({
          userCode,

          imageData: finalProfileImage,

          fileName: `${cleanFirstName}_${cleanLastName}_profile`,
        });

        if (!imageUploadResult?.success) {
          throw new Error(
            imageUploadResult?.message || "Unable to upload profile image.",
          );
        }

        finalProfileImage = imageUploadResult.imageUrl;
      }

      // =================================================
      // UPDATE PROFILE API
      // =================================================

      await updateProfileDetails({
        userCode,

        firstName: cleanFirstName,
        lastName: cleanLastName,

        email: cleanEmail,

        username: cleanUsername,

        dateOfBirth: cleanDateOfBirth,

        gender: cleanGender,

        age,

        // IMPORTANT:
        // PHP uploaded final URL
        profileImage: finalProfileImage,
      });

      /*
       * Current update API does not
       * contain gender/profile image
       * parameters, so those fields are
       * kept locally until backend adds
       * them.
       */

    saveExtraProfileFields({
      gender: cleanGender,

      profileImage: finalProfileImage,
    });

      const updatedProfile = {
        ...profile,

        user_code: userCode,

        first_name: cleanFirstName,
        last_name: cleanLastName,

        username: cleanUsername,
        email: cleanEmail,

        date_of_birth: cleanDateOfBirth,

        gender: cleanGender,

        age,

        profileImage: finalProfileImage,

        profile_image: finalProfileImage,
      };

   setProfile(updatedProfile);

   setProfileImage(finalProfileImage);

   if (typeof setUser === "function") {
     setUser((previousUser) => ({
       ...(previousUser || {}),
       ...updatedProfile,
     }));
   }

   setIsEditOpen(false);

   setSuccessMessage("Profile updated successfully.");
    } catch (error) {
      setLocalError(error?.message || "Unable to update profile.");
    }
  };

  /* =====================================================
     ERROR
  ===================================================== */

  const visibleError = localError || profileError;
  const profilePhone = normalizeString(profile?.phone || profile?.number);

  const createdDate =
    profile?.created_at ||
    profile?.createdAt ||
    profile?.created_on ||
    profile?.createdOn ||
    "";

  const customerSince = (() => {
    if (!createdDate) {
      return "";
    }

    const date = new Date(createdDate);

    if (Number.isNaN(date.getTime())) {
      return "";
    }

    return String(date.getFullYear());
  })();

  /* =====================================================
     UI
  ===================================================== */

  return (
    <>
      {/* =================================================
          PROFILE PAGE
      ================================================= */}

      {/* ================================================= */}
      {/* PROFILE PAGE */}
      {/* ================================================= */}

      <section
        className="
    w-full
    min-w-0
    overflow-hidden
    rounded-[18px]
    border
    border-[#dfe5ec]
    bg-white
    shadow-[0_3px_14px_rgba(15,23,42,0.04)]
  "
      >
        {/* ================================================= */}
        {/* PAGE HEADER */}
        {/* ================================================= */}

        <div
          className="
      flex
      flex-col
      gap-4

      border-b
      border-[#e3e8ef]

      px-5
      py-5

      sm:px-6

      md:flex-row
      md:items-center
      md:justify-between
    "
        >
          <div className="min-w-0">
            <h2 className="text-[24px] font-bold leading-tight text-[#111827] sm:text-[28px]">
              Profile
            </h2>

            <p className="mt-1.5 text-[12px] text-[#718096] sm:text-[13px]">
              Manage your personal information and account details.
            </p>
          </div>

          <div className="flex shrink-0 items-center gap-2.5">
            {/* EDIT PROFILE */}

            <button
              type="button"
              onClick={handleOpenEdit}
              disabled={profileLoading || !userCode}
              className="
          inline-flex
          h-10
          items-center
          justify-center
          gap-2

          rounded-[11px]

          border
          border-[#ccdaff]

          bg-white

          px-4

          text-[13px]
          font-semibold
          text-[#2454e6]

          shadow-[0_4px_12px_rgba(37,84,230,0.08)]

          transition

          hover:border-[#9eb7ff]
          hover:bg-[#f8faff]

          active:scale-[0.98]

          disabled:cursor-not-allowed
          disabled:opacity-50

          sm:h-11
          sm:px-5
          sm:text-[14px]
        "
            >
              <Pencil size={16} strokeWidth={2} />
              Edit Profile
            </button>

            {/* REFRESH */}

            <button
              type="button"
              onClick={loadProfile}
              disabled={profileLoading}
              aria-label="Refresh profile"
              className="
          flex
          h-10
          w-10
          items-center
          justify-center

          rounded-[11px]

          border
          border-[#e0e6ee]

          bg-white
          text-[#8491a4]

          transition

          hover:bg-[#f8fafc]
          hover:text-[#344054]

          disabled:cursor-not-allowed
          disabled:opacity-50

          sm:h-11
          sm:w-11
        "
            >
              <RefreshCcw
                size={17}
                className={profileLoading ? "animate-spin" : ""}
              />
            </button>
          </div>
        </div>

        {/* ================================================= */}
        {/* CONTENT */}
        {/* ================================================= */}

        <div className="space-y-4 p-4 sm:p-6">
          {/* ERROR */}

          {visibleError && (
            <div className="rounded-[12px] border border-red-200 bg-red-50 px-4 py-3 text-[12px] text-red-600">
              {visibleError}
            </div>
          )}

          {/* SUCCESS */}

          {successMessage && (
            <div className="rounded-[12px] border border-emerald-200 bg-emerald-50 px-4 py-3 text-[12px] text-emerald-700">
              {successMessage}
            </div>
          )}

          {/* LOADING */}

          {profileLoading && !profile?.user_code ? (
            <div className="flex min-h-[260px] flex-col items-center justify-center gap-3 text-[#8A9BB5]">
              <Loader2 size={30} className="animate-spin" />

              <p className="text-sm">Loading profile details...</p>
            </div>
          ) : (
            <>
              {/* ================================================= */}
              {/* PROFILE SUMMARY HEADER */}
              {/* EXACT GRADIENT REQUESTED */}
              {/* ================================================= */}

              <div
                className="
            flex
            min-w-0
            items-center
            gap-4

            rounded-[15px]

            border
            border-[#d7e0ef]

            px-5
            py-5

            sm:px-6
          "
                style={{
                  background:
                    "radial-gradient(circle at 94% 8%, rgba(47, 91, 234, .05), transparent 24%), linear-gradient(120deg, #fbfcff 0%, #f4f7fd 100%)",
                }}
              >
                {/* AVATAR */}

                <div
                  className="
              flex
              h-[70px]
              w-[70px]
              shrink-0
              items-center
              justify-center
              overflow-hidden

              rounded-full

              border-[4px]
              border-[#c9d3eb]

              bg-[#425da5]

              text-[20px]
              font-bold
              text-white

              shadow-[0_5px_12px_rgba(48,70,130,0.15)]

              sm:h-[82px]
              sm:w-[82px]
              sm:text-[25px]
            "
                >
                  {profile?.profile_image ? (
                    <img
                      src={profile.profile_image}
                      alt={fullName || "Profile"}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    profileInitials
                  )}
                </div>

                {/* PROFILE INFO */}

                <div className="min-w-0 flex-1">
                  {/* NAME + VERIFIED */}

                  <div className="flex min-w-0 flex-wrap items-center gap-2.5">
                    <h3
                      className="
                  min-w-0
                  truncate

                  text-[18px]
                  font-bold
                  text-[#111827]

                  sm:text-[21px]
                "
                    >
                      {fullName || profile?.username || "Customer"}
                    </h3>

                    {normalizeString(profile?.privacy_policy).toUpperCase() ===
                      "Y" && (
                      <span
                        className="
                    inline-flex
                    shrink-0
                    items-center
                    gap-1

                    rounded-full

                    border
                    border-[#aee3bc]

                    bg-[#edfff2]

                    px-2.5
                    py-1

                    text-[10px]
                    font-semibold
                    text-[#16803c]
                  "
                      >
                        <BadgeCheck size={13} strokeWidth={2} />
                        Verified
                      </span>
                    )}
                  </div>

                  {/* PHONE */}

                  <div className="mt-2 flex items-center gap-2 text-[11px] text-[#46566d] sm:text-[12px]">
                    <Phone size={14} strokeWidth={1.8} className="shrink-0" />

                    <span className="truncate">
                      {profilePhone || "Phone not added"}
                    </span>
                  </div>

                  {/* META */}

                  <div
                    className="
                mt-2
                flex
                flex-wrap
                items-center
                gap-x-2
                gap-y-1

                text-[10px]
                text-[#8190a7]

                sm:text-[11px]
              "
                  >
                    {customerSince && (
                      <>
                        <span>Customer since {customerSince}</span>

                        <span>•</span>
                      </>
                    )}

                    <span>Account ID: {userCode || "—"}</span>
                  </div>
                </div>
              </div>

              {/* ================================================= */}
              {/* PERSONAL INFORMATION */}
              {/* ================================================= */}

              <div
                className="
            overflow-hidden

            rounded-[15px]

            border
            border-[#e0e6ed]

            bg-white
          "
              >
                {/* SECTION HEADER */}

                <div className="border-b border-[#e4e9ef] px-5 py-4 sm:px-6">
                  <h3 className="text-[16px] font-bold text-[#111827]">
                    Personal Information
                  </h3>

                  <p className="mt-1 text-[11px] text-[#8190a7] sm:text-[12px]">
                    Basic details associated with your account.
                  </p>
                </div>

                {/* DETAILS */}

                <div
                  className="
              grid
              grid-cols-1

              px-5

              sm:grid-cols-2
              sm:px-6
            "
                >
                  {/* FIRST NAME */}

                  <div className="border-b border-[#e4e9ef] py-4 sm:pr-8">
                    <p className="text-[10px] font-medium uppercase text-[#7f8da3] sm:text-[11px]">
                      First Name
                    </p>

                    <p className="mt-1.5 text-[14px] font-medium text-[#111827] sm:text-[15px]">
                      {profile?.first_name || "Not added"}
                    </p>
                  </div>

                  {/* LAST NAME */}

                  <div className="border-b border-[#e4e9ef] py-4 sm:pl-8">
                    <p className="text-[10px] font-medium uppercase text-[#7f8da3] sm:text-[11px]">
                      Last Name
                    </p>

                    <p className="mt-1.5 text-[14px] font-medium text-[#111827] sm:text-[15px]">
                      {profile?.last_name || "Not added"}
                    </p>
                  </div>

                  {/* EMAIL */}

                  <div className="border-b border-[#e4e9ef] py-4 sm:pr-8">
                    <p className="text-[10px] font-medium uppercase text-[#7f8da3] sm:text-[11px]">
                      Email Address
                    </p>

                    <p className="mt-1.5 break-all text-[14px] font-medium text-[#111827] sm:text-[15px]">
                      {profile?.email || "Not added"}
                    </p>
                  </div>

                  {/* GENDER */}

                  <div className="border-b border-[#e4e9ef] py-4 sm:pl-8">
                    <p className="text-[10px] font-medium uppercase text-[#7f8da3] sm:text-[11px]">
                      Gender
                    </p>

                    <p className="mt-1.5 text-[14px] font-medium text-[#111827] sm:text-[15px]">
                      {profile?.gender || "Not added"}
                    </p>
                  </div>

                  {/* DOB */}

                  <div className="py-4 sm:pr-8">
                    <p className="text-[10px] font-medium uppercase text-[#7f8da3] sm:text-[11px]">
                      Date Of Birth
                    </p>

                    <p className="mt-1.5 text-[14px] font-medium text-[#111827] sm:text-[15px]">
                      {formatDate(profile?.date_of_birth)}
                    </p>
                  </div>

                  {/* AGE */}

                  <div className="border-t border-[#e4e9ef] py-4 sm:border-t-0 sm:pl-8">
                    <p className="text-[10px] font-medium uppercase text-[#7f8da3] sm:text-[11px]">
                      Age
                    </p>

                    <p className="mt-1.5 text-[14px] font-medium text-[#111827] sm:text-[15px]">
                      {profile?.age || calculateAge(profile?.date_of_birth)
                        ? `${
                            profile?.age || calculateAge(profile?.date_of_birth)
                          } years`
                        : "Not added"}
                    </p>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </section>

      {/* =================================================
          EDIT PROFILE MODAL
      ================================================= */}

      {isEditOpen && (
        <div
          className="
            fixed
            inset-0
            z-[99999]
            flex
            h-[100dvh]
            w-full
            items-center
            justify-center
            overflow-hidden
            bg-black/55
            p-2
            backdrop-blur-[2px]

            sm:p-4
            lg:p-6
          "
        >
          {/* BACKDROP */}

          <button
            type="button"
            aria-label="Close edit profile"
            onClick={handleCloseEdit}
            className="absolute inset-0 cursor-default bg-transparent"
          />

          {/* MODAL */}

          <form
            onSubmit={handleSaveProfile}
            className="
              relative
              z-10
              flex
              max-h-[calc(100dvh-16px)]
              w-full
              max-w-[760px]
              flex-col
              overflow-hidden
              rounded-[20px]
              bg-white
              shadow-2xl

              sm:max-h-[calc(100dvh-32px)]
              sm:rounded-[24px]
              lg:max-h-[calc(100dvh-48px)]
            "
          >
            {/* ===========================================
                HEADER
            ============================================ */}

            <div className="flex shrink-0 items-start justify-between gap-3 border-b border-[#edf0f4] px-4 py-3.5 sm:gap-4 sm:px-6 sm:py-5">
              <div className="min-w-0">
                <h3 className="text-lg font-semibold leading-6 text-[#273142] sm:text-[20px]">
                  Edit Profile
                </h3>

                <p className="mt-1 max-w-[520px] text-xs leading-5 text-[#8ba0bf] sm:text-[13px]">
                  Update your profile details and image.
                </p>
              </div>

              <button
                type="button"
                onClick={handleCloseEdit}
                disabled={profileLoading}
                aria-label="Close"
                className="
                  flex
                  h-9
                  w-9
                  sm:h-10
                  sm:w-10
                  shrink-0
                  items-center
                  justify-center
                  rounded-full
                  border
                  border-[#e1e6ec]
                  bg-white
                  text-[#657387]
                  transition

                  hover:bg-[#f7f8fa]

                  disabled:cursor-not-allowed
                  disabled:opacity-50
                "
              >
                <X size={19} />
              </button>
            </div>

            {/* ===========================================
                SCROLLABLE CONTENT
            ============================================ */}

            <div
              className="
                min-h-0
                flex-1
                overscroll-contain
                overflow-y-auto
                px-4
                py-4

                [scrollbar-width:none]
                [-ms-overflow-style:none]
                [&::-webkit-scrollbar]:hidden

                sm:px-6
                sm:py-5
                lg:px-7
              "
            >
              {/* =========================================
                  PROFILE IMAGE AREA
              ========================================== */}

              <div
                className="
                  flex
                  min-h-[142px]
                  flex-col
                  items-center
                  justify-center
                  rounded-[16px]
                  border
                  border-[#edf0f4]
                  bg-[#fafbfc]
                  px-4
                  py-4

                  sm:min-h-[152px]
                  sm:py-5
                "
              >
                <div className="relative">
                  {/* IMAGE / INITIALS */}

                  <div
                    className="
                      flex
                      h-[82px]
                      w-[82px]
                      sm:h-[94px]
                      sm:w-[94px]
                      items-center
                      justify-center
                      overflow-hidden
                      rounded-full
                      border-[3px]
                      border-white
                      bg-[#f4f5f7]
                      text-[20px]
                      font-semibold
                      text-[#21195A]
                      shadow-md
                    "
                  >
                    {profileImage ? (
                      <img
                        src={profileImage}
                        alt="Profile preview"
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      editProfileInitials
                    )}
                  </div>

                  {/* CAMERA */}

                  <button
                    type="button"
                    onClick={() => profileImageInputRef.current?.click()}
                    aria-label="Change profile image"
                    className="
                      absolute
                      -bottom-1
                      right-0
                      flex
                      h-8
                      w-8
                      sm:h-9
                      sm:w-9
                      items-center
                      justify-center
                      rounded-full
                      border-[3px]
                      border-white
                      bg-[#21195A]
                      text-white
                      shadow-md
                      transition

                      hover:bg-[#30256f]
                      active:scale-95
                    "
                  >
                    <Camera size={16} />
                  </button>

                  <input
                    ref={profileImageInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/webp"
                    onChange={handleProfileImageSelect}
                    className="hidden"
                  />
                </div>

                <button
                  type="button"
                  onClick={() => profileImageInputRef.current?.click()}
                  className="mt-3 text-[12px] font-medium text-[#8ba0bf] transition hover:text-[#21195A]"
                >
                  Click camera to change profile image
                </button>

                {imageError && (
                  <p className="mt-2 text-[11px] text-red-500">{imageError}</p>
                )}
              </div>

              {/* =========================================
                  FORM ERROR
              ========================================== */}

              {visibleError && (
                <div className="mt-4 rounded-[10px] border border-red-200 bg-red-50 px-3 py-2.5 text-[12px] text-red-600">
                  {visibleError}
                </div>
              )}

              {/* =========================================
                  FIRST + LAST NAME
              ========================================== */}

              <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
                {/* FIRST NAME */}

                <div>
                  <label className="mb-2 block text-[13px] font-medium text-[#344054]">
                    First name
                  </label>

                  <input
                    type="text"
                    value={firstName}
                    onChange={handleFirstNameChange}
                    autoComplete="given-name"
                    placeholder="Enter first name"
                    maxLength={40}
                    className="
                      h-11
                      w-full
                      rounded-[11px]
                      border
                      border-[#dce2e9]
                      bg-white
                      px-3
                      text-base
                      text-[#344054]
                      sm:text-[14px]
                      outline-none
                      transition

                      placeholder:text-[#98a2b3]

                      focus:border-[#21195A]
                      focus:ring-1
                      focus:ring-[#21195A]/10
                    "
                  />
                </div>

                {/* LAST NAME */}

                <div>
                  <label className="mb-2 block text-[13px] font-medium text-[#344054]">
                    Last name
                  </label>

                  <input
                    type="text"
                    value={lastName}
                    onChange={handleLastNameChange}
                    autoComplete="family-name"
                    placeholder="Enter last name"
                    maxLength={40}
                    className="
                      h-11
                      w-full
                      rounded-[11px]
                      border
                      border-[#dce2e9]
                      bg-white
                      px-3
                      text-base
                      text-[#344054]
                      sm:text-[14px]
                      outline-none
                      transition

                      placeholder:text-[#98a2b3]

                      focus:border-[#21195A]
                      focus:ring-1
                      focus:ring-[#21195A]/10
                    "
                  />
                </div>
              </div>

              {/* =========================================
                  EMAIL
              ========================================== */}

              <div className="mt-4">
                <label className="mb-2 block text-[13px] font-medium text-[#344054]">
                  Email
                </label>

                <input
                  type="email"
                  value={email}
                  onChange={(event) => {
                    setEmail(event.target.value.slice(0, 120));

                    setLocalError("");
                  }}
                  autoComplete="email"
                  placeholder="Enter email"
                  maxLength={120}
                  className="
                    h-11
                    w-full
                    rounded-[11px]
                    border
                    border-[#dce2e9]
                    bg-white
                    px-3
                    text-base
                    text-[#344054]
                    sm:text-[14px]
                    outline-none
                    transition

                    placeholder:text-[#98a2b3]

                    focus:border-[#21195A]
                    focus:ring-1
                    focus:ring-[#21195A]/10
                  "
                />
              </div>

              {/* =========================================
                  DOB + AGE
              ========================================== */}

              <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
                {/* DOB */}

                <div>
                  <label className="mb-2 block text-[13px] font-medium text-[#344054]">
                    Date of birth
                  </label>

                  <input
                    type="date"
                    value={dateOfBirth}
                    max={getTodayDate()}
                    onChange={(event) => {
                      const value = event.target.value;

                      setDateOfBirth(value);

                      // Auto-fill age from DOB.
                      // User can still manually edit it afterward.
                      setAge(calculateAge(value));

                      setLocalError("");
                    }}
                    className="
                      h-11
                      w-full
                      rounded-[11px]
                      border
                      border-[#dce2e9]
                      bg-white
                      px-3
                      text-base
                      text-[#344054]
                      sm:text-[14px]
                      outline-none
                      transition

                      focus:border-[#21195A]
                      focus:ring-1
                      focus:ring-[#21195A]/10
                    "
                  />
                </div>

                {/* AGE */}

                <div>
                  <label className="mb-2 block text-[13px] font-medium text-[#344054]">
                    Age
                  </label>

                  <input
                    type="number"
                    value={age}
                    min="0"
                    max="120"
                    inputMode="numeric"
                    placeholder="Enter age"
                    onChange={(event) => {
                      const value = event.target.value;

                      if (value === "") {
                        setAge("");
                        setLocalError("");
                        return;
                      }

                      const numericAge = Number(value);

                      if (
                        Number.isInteger(numericAge) &&
                        numericAge >= 0 &&
                        numericAge <= 120
                      ) {
                        setAge(value);
                      }

                      setLocalError("");
                    }}
                    className="
    h-11
    w-full
    rounded-[11px]
    border
    border-[#dce2e9]
    bg-white
    px-3
    text-base
    text-[#344054]
    outline-none
    transition

    sm:text-[14px]

    placeholder:text-[#98a2b3]

    focus:border-[#21195A]
    focus:ring-1
    focus:ring-[#21195A]/10
  "
                  />
                </div>

                <div>
                  <label className="mb-2 block text-[13px] font-medium text-[#344054]">
                    Gender
                  </label>

                  <select
                    value={gender}
                    onChange={(event) => {
                      setGender(event.target.value);

                      setLocalError("");
                    }}
                    className="
                    h-11
                    w-full
                    cursor-pointer
                    rounded-[11px]
                    border
                    border-[#dce2e9]
                    bg-white
                    px-3
                    text-base
                    text-[#344054]
                    sm:text-[14px]
                    outline-none
                    transition

                    focus:border-[#21195A]
                    focus:ring-1
                    focus:ring-[#21195A]/10
                  "
                  >
                    <option value="">Select gender</option>

                    <option value="Male">Male</option>

                    <option value="Female">Female</option>

                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>
            </div>

            {/* ===========================================
                STICKY FOOTER
            ============================================ */}

            <div
              className="
                flex
                shrink-0
                flex-col-reverse
                items-stretch
                justify-end
                gap-2.5

                sm:flex-row
                sm:items-center
                sm:gap-3
                border-t
                border-[#edf0f4]
                bg-white
                px-4
                py-3.5

                pb-[max(16px,env(safe-area-inset-bottom))]

                sm:px-6
                sm:py-4
              "
            >
              {/* CANCEL */}

              <button
                type="button"
                onClick={handleCloseEdit}
                disabled={profileLoading}
                className="
                  h-11
                  w-full
                  rounded-full
                  border
                  border-[#dfe4ea]
                  bg-white
                  px-5
                  text-[13px]
                  font-medium
                  text-[#344054]
                  transition

                  hover:bg-[#f7f8fa]

                  disabled:cursor-not-allowed
                  disabled:opacity-50

                  sm:w-auto
                  sm:min-w-[96px]
                "
              >
                Cancel
              </button>

              {/* SAVE */}

              <button
                type="submit"
                disabled={profileLoading}
                className="
                  inline-flex
                  h-11
                  w-full
                  items-center
                  justify-center
                  gap-2
                  rounded-full
                  bg-[#21195A]
                  px-5
                  text-[13px]
                  font-semibold
                  text-white
                  transition

                  hover:bg-[#30256f]

                  disabled:cursor-not-allowed
                  disabled:opacity-50

                  sm:w-auto
                  sm:min-w-[158px]
                "
              >
                {profileLoading ? (
                  <Loader2 size={17} className="animate-spin" />
                ) : (
                  <Save size={17} />
                )}

                {profileLoading ? "Saving..." : "Save changes"}
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}
